import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function LouderMixWithoutClippingLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What loudness really means",
      text: "A louder mix is not just a higher peak level. It feels fuller, denser, and more controlled at the same playback volume.",
      icon: <VolumeUpRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "What causes clipping",
      text: "Clipping happens when peaks exceed the available headroom, creating distortion that may sound harsh, broken, or uncontrolled.",
      icon: <BoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Control the mix before the limiter. Loudness starts with balance, arrangement, EQ, dynamics, and clean gain staging.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const commonProblems = [
    {
      title: "The limiter is working too hard",
      description:
        "If the limiter is doing all the loudness work, the mix can get flat, harsh, distorted, or smaller instead of stronger.",
    },
    {
      title: "The low end eats the headroom",
      description:
        "Kick, bass, subs, and low-mid buildup can trigger limiters early, leaving less room for the rest of the mix.",
    },
    {
      title: "The mix clips before it sounds loud",
      description:
        "Sharp peaks may hit the ceiling quickly even though the average level still feels low. This is common with drums, percussion, and transient-heavy material.",
    },
    {
      title: "The master sounds loud but worse",
      description:
        "A louder master is not automatically better. If the punch, depth, or tone collapses, the processing is probably taking too much from the mix.",
    },
  ];

  const steps = [
    {
      title: "Start with the mix balance",
      description:
        "Before adding mastering processors, make sure the mix itself is balanced. If one element is too loud, too boomy, or too sharp, the final loudness chain will react to that problem instead of the song.",
    },
    {
      title: "Leave clean headroom",
      description:
        "Your mix does not need to peak right at 0 dB before mastering. Leave space on the master bus so processors can work without immediately clipping. A clean mix with headroom is easier to make loud than a mix already pinned to the ceiling.",
    },
    {
      title: "Control low-end buildup",
      description:
        "Low frequencies carry a lot of energy. If the kick and bass are too large or uncontrolled, the limiter will react to the low end before the mix feels loud. Tighten the low end first so the loudness chain has room to work.",
    },
    {
      title: "Use EQ to remove clutter",
      description:
        "A muddy mix is harder to make loud. Low-mid buildup, harsh resonances, and crowded frequency ranges all reduce clarity. Small cleanup moves can make the mix feel louder without adding gain.",
    },
    {
      title: "Manage peaks before the final limiter",
      description:
        "Transient-heavy sounds can create high peaks that do not always add useful loudness. Gentle clipping, transient shaping, or compression can reduce peak spikes before the final limiter has to catch them.",
    },
    {
      title: "Use compression for control, not punishment",
      description:
        "Bus compression can help glue a mix together and reduce dynamic jumps, but too much can flatten the groove. Use it to support the mix, not force loudness at the cost of movement.",
    },
    {
      title: "Use clipping carefully",
      description:
        "Clipping can shave sharp peaks and increase perceived loudness, especially on drums or the mix bus. The key is subtlety. If the tone gets brittle, crunchy, or smaller, back it off.",
    },
    {
      title: "Use limiting as the final ceiling",
      description:
        "The limiter should catch final peaks and raise the average level, but it should not be asked to fix the whole mix. A limiter works best when the mix is already controlled before it gets there.",
    },
    {
      title: "Compare at matched volume",
      description:
        "Louder almost always sounds better at first. Turn the processed version down to match the unprocessed level and check whether it actually sounds better, not just louder.",
    },
  ];

  const loudnessTools = [
    {
      title: "Gain staging",
      description:
        "Keeps signals at sensible levels throughout the mix so processors behave predictably and the master bus has room to work.",
    },
    {
      title: "EQ cleanup",
      description:
        "Removes unnecessary buildup and harshness, making the mix feel clearer and louder without only adding gain.",
    },
    {
      title: "Compression",
      description:
        "Controls dynamic movement and can add density, but heavy compression can flatten punch if pushed too hard.",
    },
    {
      title: "Clipping",
      description:
        "Shaves short peaks before they hit the limiter, often helping drums and full mixes get louder with less limiter strain.",
    },
    {
      title: "Limiting",
      description:
        "Sets the final ceiling and raises overall level, but works best after the mix is already balanced and controlled.",
    },
  ];

  const frequencyAreas = [
    {
      range: "30–80 Hz",
      title: "Sub and low-end pressure",
      description:
        "Too much energy here can eat headroom fast, especially from kick, bass, and sub-heavy instruments.",
    },
    {
      range: "150–400 Hz",
      title: "Low-mid density",
      description:
        "Can add weight, but too much makes the mix cloudy and harder to make loud cleanly.",
    },
    {
      range: "2–5 kHz",
      title: "Presence and bite",
      description:
        "Helps a mix feel forward, but harsh peaks here can become painful when the master gets louder.",
    },
    {
      range: "6–10 kHz",
      title: "Sharpness and sibilance",
      description:
        "Cymbals, vocals, and bright transients can get aggressive when limiting is pushed.",
    },
    {
      range: "10 kHz+",
      title: "Air and openness",
      description:
        "Can help a mix feel clear, but it should not be used to compensate for deeper balance problems.",
    },
  ];

  const bestPractices = [
    "Do not rely on the final limiter to fix mix problems.",
    "Control kick, bass, and low-mid buildup before chasing loudness.",
    "Use clipping or transient control before limiting when peaks are too sharp.",
    "Compare loudness changes at matched volume so you are not fooled by extra gain.",
    "Push loudness in stages instead of asking one processor to do everything.",
    "Keep checking punch, low end, vocal clarity, and stereo width as the mix gets louder.",
    "Stop when the mix starts getting smaller, harsher, or less exciting.",
  ];

  const relatedLinks = [
    { label: "Limiting", to: "/learning/limiting" },
    { label: "Clipping vs Limiting", to: "/learning/clipping-vs-limiting" },
    { label: "Mastering Basics", to: "/learning/mastering-basics" },
    { label: "Gain Staging", to: "/learning/gain-staging" },
    { label: "Compression", to: "/learning/compression" },
    { label: "How to Fix a Muddy Mix", to: "/learning/fix-muddy-mix" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="How to Make a Mix Louder Without Clipping | Parish Audio Learning"
        description="Learn how to make a mix louder without clipping using better balance, gain staging, EQ cleanup, compression, clipping, and limiting."
        canonical="https://parishaudio.com/learning/how-to-make-a-mix-louder-without-clipping"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about making a mix louder without clipping"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/how-to-make-a-mix-louder-without-clipping"
        headline="How to Make a Mix Louder Without Clipping"
        description="Learn how to make a mix louder without clipping using better balance, gain staging, EQ cleanup, compression, clipping, and limiting."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        keywords={[
          "make a mix louder",
          "mix louder without clipping",
          "loudness",
          "clipping",
          "limiting",
          "mastering",
          "gain staging",
          "mix bus processing",
          "audio mixing",
          "music production",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "How to Make a Mix Louder Without Clipping",
            item: "https://parishaudio.com/learning/how-to-make-a-mix-louder-without-clipping",
          },
        ]}
        datePublished="2026-05-02T00:00:00-06:00"
        dateModified="2026-05-02T00:00:00-06:00"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Loudness & Mastering Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      How to Make a Mix Louder Without Clipping
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 740,
                      }}
                    >
                      Making a mix louder is not just about pushing the master fader into a
                      limiter. A loud mix needs balance, headroom, clean low end, controlled
                      peaks, and processing that adds density without breaking the sound.
                      The goal is more impact, not just more level.
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      Published May 2, 2026
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.55 }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Why Loudness Is More Than Peak Level
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Peak level tells you how close the loudest moments are to the ceiling,
                    but it does not tell you how loud the mix feels. A mix can peak near
                    0 dB and still sound quiet if the average level is low, the low end is
                    uncontrolled, or important parts are not dense enough.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Perceived loudness comes from balance, density, clarity, and control.
                    If the mix is clean and organized before the final limiter, it can get
                    louder with less damage. If the mix is messy, the loudness chain will
                    usually make the problems more obvious.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common Loudness Problems
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {commonProblems.map((problem) => (
                      <Paper key={problem.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.5 }}>
                          {problem.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {problem.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Step-by-Step: Getting Louder Cleanly
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {steps.map((step, index) => (
                        <Paper key={step.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {step.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Frequency Areas That Affect Loudness
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      These ranges are starting points. The best move depends on the mix,
                      arrangement, and target sound.
                    </Typography>

                    <Stack spacing={1.2}>
                      {frequencyAreas.map((area) => (
                        <Paper key={area.range} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.7}>
                            <Chip
                              label={area.range}
                              size="small"
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: "5px",
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontWeight: 700,
                              }}
                            />
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {area.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {area.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompressRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Clipping and Limiting Are Not the Same Job
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Clipping and limiting both control peaks, but they do it differently.
                    A limiter reacts dynamically and holds peaks under a ceiling. Clipping
                    cuts off peak tips more directly. Used carefully, clipping can reduce
                    short transients before the limiter, which lets the limiter work less
                    aggressively.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    That does not mean clipping should be pushed until it is obvious. The
                    best clipping is often subtle. It removes just enough peak information
                    to make the mix easier to control while keeping the tone, punch, and
                    movement intact.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SpeedRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Tools That Help With Loudness
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Loudness usually works best in stages. Each tool should do a focused
                      job instead of forcing one processor to carry the whole master.
                    </Typography>

                    <Stack spacing={1.2}>
                      {loudnessTools.map((tool) => (
                        <Paper key={tool.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.4 }}>
                            {tool.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {tool.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: accent }} />
                          <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                            Quick listening test
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                          After making the mix louder, turn it down to match the original
                          version. If it still feels clearer, stronger, and more controlled,
                          the loudness processing is helping. If it only wins when louder,
                          back off and rebalance.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LibraryMusicRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tools That Help
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Peakline is built for this kind of loudness shaping. It can help control
                    peaks, add density, and push perceived level while keeping the process
                    focused and easy to judge in context.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    It works best when the mix is already balanced and you want to bring
                    the final level forward without relying on one limiter to do everything.
                  </Typography>

                  <Button
                    component={RouterLink}
                    to="/products/peakline"
                    sx={{
                      alignSelf: "flex-start",
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    View Peakline
                  </Button>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                    >
                      Related Topics
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    These articles connect directly to loudness, mastering, clipping,
                    limiting, and mix control.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {relatedLinks.map((link) => (
                      <Button
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}